import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from './useAuth';

import {
  IMPERSONATION_COPY,
  impersonatorOf,
  leaveImpersonation,
} from '@/utils/impersonation';

export interface UseSignOutReturn {
  /** True while an admin is signed in as this member from the admin panel. */
  isImpersonating: boolean;
  /** The row's label: Sign Out, or "Leave cleo's account" while impersonating. */
  label: string | null;
  /** Sign out, or leave the member's account for the admin panel. */
  signOut: () => Promise<void>;
}

/**
 * Every Sign Out affordance (user menu, drawer, command palette) goes through
 * here, so an admin signed in as a member leaves instead: `/logout` is a write,
 * and the session is read-only. `label` is null when the caller's own label
 * stands.
 */
export function useSignOut(): UseSignOutReturn {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const impersonator = impersonatorOf(user);

  const signOut = useCallback(async () => {
    if (impersonator) {
      await leaveImpersonation();
      return;
    }
    await logout();
    navigate('/login');
  }, [impersonator, logout, navigate]);

  return {
    isImpersonating: impersonator !== null,
    label:
      impersonator && user
        ? IMPERSONATION_COPY.leaveAccount(user.username)
        : null,
    signOut,
  };
}
